import React from 'react';

import { Formik, FormikErrors, FormikHelpers } from 'formik';
import { observer } from 'mobx-react';
import { reverse } from 'named-urls';

import { Button } from 'components/Button';
import { CenteredCircularProgress } from 'components/Progress';
import CustomModal from 'components/CustomModal';
import CustomTextInputField from 'components/SharedFormComponents/CustomTextInputField';
import EntityDropdownField from 'components/SharedFormComponents/EntityDropdownField';
import FieldHelpIcon from 'components/FieldHelpIcon';
import CustomUserDropdownField from 'components/SharedFormComponents/CustomUserDropdownField';

import EntitiesFormField from 'containers/EntitiesFormSubmission/components/EntitiesFormField';

import { EntityModules, ProtectorType } from 'core/constants';
import routes from 'core/routes';

import { EntityEnumsOptions } from 'utils/api/modules/entities/entities.types';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { useStore } from 'utils/hooks/store';
import { ChoiceValue } from 'utils/library';

import { CONTROL_FIELD, CONTROL_OWNER_FILTER, ControlStatus, DEFAULT_DUE_HOUR, STATUS_HELP } from '../../constants';
import { LoadError } from '../../styles';

import FormRow from './FormRow';
import { CreateControlValues, createControlSchema, toFieldValues, withDefaultDueTime } from './schema';

type CreateControlModalProps = {
  onClose: () => void;
};

const activeOptions = (options: EntityEnumsOptions[] | undefined): EntityEnumsOptions[] =>
  (options ?? []).filter((option) => option.active);

const CreateControlModal: React.FunctionComponent<CreateControlModalProps> = ({ onClose }): React.ReactElement => {
  const { controlDetails, entityFields, routing } = useStore();

  const [fieldsRequested, setFieldsRequested] = React.useState(false);

  const loadFields = React.useCallback(async (): Promise<void> => {
    setFieldsRequested(false);
    await entityFields.loadFields(EntityModules.Controls);
    setFieldsRequested(true);
  }, [entityFields]);

  React.useEffect(() => {
    loadFields();
  }, [loadFields]);

  const fieldById = (key: string) => entityFields.data.find((field) => field.id === key);
  const categories = activeOptions(fieldById(CONTROL_FIELD.category)?.enum as EntityEnumsOptions[] | undefined);
  const statuses = activeOptions(fieldById(CONTROL_FIELD.status)?.enum as EntityEnumsOptions[] | undefined);
  const inPlace = statuses.find((option) => option.key === ControlStatus.InPlace);

  const initialValues: CreateControlValues = {
    name: '',
    description: '',
    category: null,
    owner: null,
    status: inPlace ? [{ ...inPlace }] : null,
    dueDate: null,
  };

  const onSubmit = async (
    values: CreateControlValues,
    { setErrors, setSubmitting }: FormikHelpers<CreateControlValues>
  ): Promise<void> => {
    const created = await controlDetails.createControl(values.name.trim(), toFieldValues(values));

    if (typeof created === 'string') {
      onClose();
      routing.push(reverse(routes.dashboard.risks.controlDetails, { entityId: created }));
      return;
    }

    const serverErrors: FormikErrors<CreateControlValues> = {
      name: controlDetails.fieldErrors[CONTROL_FIELD.name],
      description: controlDetails.fieldErrors[CONTROL_FIELD.description],
      category: controlDetails.fieldErrors[CONTROL_FIELD.category],
      owner: controlDetails.fieldErrors[CONTROL_FIELD.owner],
      status: controlDetails.fieldErrors[CONTROL_FIELD.status],
      dueDate: controlDetails.fieldErrors[CONTROL_FIELD.dueDate],
    };
    setErrors(serverErrors);
    setSubmitting(false);
  };

  if (!inPlace) {
    return (
      <CustomModal
        openModal
        onClose={onClose}
        modalTitle="New control"
        cancelButton={
          <Button variant="outlined" size="large" onClick={onClose}>
            Cancel
          </Button>
        }
      >
        {fieldsRequested ? (
          <LoadError data-testid="create-control-load-error">
            <span>The form could not be loaded.</span>
            <Button variant="contained" onClick={loadFields}>
              Retry
            </Button>
          </LoadError>
        ) : (
          <CenteredCircularProgress />
        )}
      </CustomModal>
    );
  }

  return (
    <Formik<CreateControlValues>
      initialValues={initialValues}
      validationSchema={createControlSchema}
      validateOnMount
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, setFieldValue, setFieldTouched, isSubmitting, isValid, handleSubmit }) => {
        const showError = (key: keyof CreateControlValues): string | undefined =>
          touched[key] ? (errors[key] as string | undefined) : undefined;
        const proposed = values.status?.[0]?.key === ControlStatus.Proposed;

        return (
          <CustomModal
            openModal
            onClose={onClose}
            modalTitle="New control"
            cancelButton={
              <Button variant="outlined" size="large" onClick={onClose}>
                Cancel
              </Button>
            }
            submitButton={
              <Button
                variant="contained"
                size="large"
                data-testid="create-control-submit"
                disabled={isSubmitting || !isValid}
                onClick={() => handleSubmit()}
              >
                Add control
              </Button>
            }
          >
            <div data-testid="create-control-modal">
              <FormRow testId="create-control-name" label="Control">
                <CustomTextInputField
                  value={values.name}
                  description="Control"
                  onChange={(value: AnswerTypes) => setFieldValue('name', value)}
                  requiredNotFilled={Boolean(showError('name'))}
                  errorMessage={showError('name')}
                  inputProps={{ autoFocus: true }}
                />
              </FormRow>
              <FormRow testId="create-control-description" label="Description">
                <CustomTextInputField
                  value={values.description}
                  description="What the control is and how it works"
                  multiline
                  minRows={4}
                  onChange={(value: AnswerTypes) => setFieldValue('description', value)}
                  requiredNotFilled={Boolean(showError('description'))}
                  errorMessage={showError('description')}
                />
              </FormRow>
              <FormRow testId="create-control-category" label="Category">
                <EntityDropdownField
                  type={ProtectorType.SingleChoice}
                  value={values.category}
                  items={{ enum: categories }}
                  description="Category"
                  customPlaceholder="Select category"
                  disablePortal={false}
                  requiredNotFilled={Boolean(showError('category'))}
                  onChange={(value) => {
                    setFieldTouched('category', true, false);
                    setFieldValue('category', value as unknown as ChoiceValue);
                  }}
                />
              </FormRow>
              <FormRow testId="create-control-owner" label="Owner">
                <CustomUserDropdownField
                  value={values.owner}
                  description="Owner"
                  customPlaceholder="Select owner"
                  licenses={CONTROL_OWNER_FILTER.licenses}
                  userRole={CONTROL_OWNER_FILTER.role}
                  onChange={(value) => setFieldValue('owner', value)}
                  requiredNotFilled={Boolean(showError('owner'))}
                  errorMessage={showError('owner')}
                />
              </FormRow>
              <FormRow
                testId="create-control-status"
                label="Status"
                labelAddon={<FieldHelpIcon name="Status" helpText={STATUS_HELP} />}
              >
                <EntityDropdownField
                  type={ProtectorType.SingleChoice}
                  value={values.status}
                  items={{ enum: statuses }}
                  description="Status"
                  disablePortal={false}
                  requiredNotFilled={Boolean(showError('status'))}
                  onChange={(value) => setFieldValue('status', value as unknown as ChoiceValue)}
                />
              </FormRow>
              {proposed && (
                <FormRow testId="create-control-due-date" label="Due date">
                  <EntitiesFormField
                    type={ProtectorType.DateTime}
                    value={values.dueDate}
                    description="Due date"
                    items={[]}
                    onChange={(value: AnswerTypes) =>
                      setFieldValue(
                        'dueDate',
                        withDefaultDueTime(values.dueDate, value as number | null, DEFAULT_DUE_HOUR)
                      )
                    }
                    requiredNotFilled={Boolean(showError('dueDate'))}
                    errorMessage={showError('dueDate')}
                  />
                </FormRow>
              )}
            </div>
          </CustomModal>
        );
      }}
    </Formik>
  );
};

export default observer(CreateControlModal);
